import {
  AnalyticsAndReport,
  CertificateIcon1,
  DashboardIcon,
  DeveloperIcon,
  IssueIcon,
  ManageCredentails,
  ManageTemplateIcon,
  QRIcon,
  SettingsIcon,
  UserManagementIcon,
  VerificationIcon,
} from "assets";
import { Images } from "assets/Images";
import avatar from "assets/Resources/avatar.svg";
import IsPasswordResetIn60Days from "components/CheckPassReset";
import { Sidebar } from "components/Molecules/Siderbar/Sidebar";
import ScrollToTop from "components/ScrollToTop";
import UserInActive from "components/UserInActive";

import { AuthProvider, useAuth } from "providers/AuthProvider";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ROLES, USER_ROLES } from "constants/contants";
import React, { Suspense, lazy } from "react";
import Loading from "components/Molecules/Loading";

// Lazy load all major pages
const AdminAnalyticsPage = lazy(() => import("pages/Admin/Analytics"));
const AdminDashBoard = lazy(() => import("pages/Admin/Dashboard"));
const DeveloperApi = lazy(() => import("pages/Admin/Developer/Api"));
const DeveloperWebhook = lazy(() => import("pages/Admin/Developer/Webhook"));
const HolderOnBoarding = lazy(() => import("pages/Admin/OnBoarding"));
const AdminAuthentication = lazy(
  () => import("pages/Admin/Settings/Authentication")
);
const AdminOrganization = lazy(
  () => import("pages/Admin/Settings/Organization")
);
const UserManagement = lazy(() => import("pages/Admin/UserManagement"));
const ForgetPassword = lazy(
  () => import("pages/Authentication/forgetPassword")
);
const LoginToCertify = lazy(
  () => import("pages/Authentication/loginToCertify")
);
const ResetPassword = lazy(() => import("pages/Authentication/resetPassword"));
const TwoFactorAuth = lazy(() => import("pages/Authentication/twoFactorAuth"));
const Certificate = lazy(() => import("pages/Certificate"));
const CertificateDesigner = lazy(
  () => import("pages/Certificate/CertificateDeign")
);
const ManageTemplate = lazy(() => import("pages/Issuer/ManageTemplate"));
const DashboardIssuer = lazy(() => import("pages/Issuer/Dashboard/Index"));
const IssueCredential = lazy(() => import("pages/Issuer/IssueCredential"));
const ManageCredentials = lazy(() => import("pages/Issuer/ManageCredentials"));
const ReportsAndAnalytics = lazy(
  () => import("pages/Issuer/ReportsAndAnalytics/ReportsAndAnalytics")
);
const LandingPage = lazy(() => import("pages/LandingPage"));
const NotFound = lazy(() => import("pages/NotFound"));
const Organization = lazy(() => import("pages/Settings/Organization"));
const Authentication = lazy(() => import("pages/Settings/authentication"));
const Profile = lazy(() => import("pages/Settings/profile"));
const SuperAdminAnalytics = lazy(() => import("pages/SuperAdmin/Analytics"));
const CustomerManagement = lazy(
  () => import("pages/SuperAdmin/CustomerManagement")
);
const SuperAdminDashBoard = lazy(() => import("pages/SuperAdmin/Dashboard"));
const TermsAndConditions = lazy(() => import("pages/TermsAndConditions"));
const SharedCredential = lazy(() => import("pages/Verification"));
const ManageSchema = lazy(() => import("pages/Verifier/ManageSchema"));
const Verify = lazy(() => import("pages/Verifier/Verify"));
const AnalyticsAndReports = lazy(
  () => import("pages/Verifier/AnalyticsAndReport/Index")
);
const DashboardVerifier = lazy(() => import("pages/Verifier/Dashboard"));
const InitiateVerification = lazy(
  () => import("pages/Verifier/InitiateVerification")
);
const QR = lazy(() => import("pages/Verifier/QR"));
const VerifierAuthentication = lazy(
  () => import("pages/Verifier/Settings/Authentication")
);
const VerifierOrganization = lazy(
  () => import("pages/Verifier/Settings/Organization")
);
const PrivacyPolicy = lazy(() => import("pages/PrivacyAndPolicy"));

const IssuerDashboard = () => {
  const { t } = useTranslation();

  const { user } = useAuth();
  let menuOptions = [
    {
      name: t("tabs.dashboard"),
      leftIcon: <DashboardIcon />,
      path: "/issuer/dashboard",
    },
    {
      name: t("tabs.issueCredential"),
      leftIcon: <IssueIcon />,
      path: "/issuer/issue-credential",
    },
    {
      name: t("tabs.templates"),
      leftIcon: <ManageTemplateIcon />,
      path: "/issuer/manage-template",
    },
    {
      name: t("tabs.manageCredentials"),
      leftIcon: <ManageCredentails />,
      path: "/issuer/manage-credentials",
    },
    {
      name: t("tabs.analyticsAndReport"),
      leftIcon: <AnalyticsAndReport />,
      path: "/issuer/analytics",
    },
    {
      name: "Designs",
      leftIcon: <CertificateIcon1 />,
      path: "/issuer/designs",
    },
    {
      name: t("tabs.settings"),
      leftIcon: <SettingsIcon />,
      subMenu: [
        {
          name: t("tabs.myProfile"),
          path: "/issuer/profile",
        },
        {
          name: t("tabs.authentication"),
          path: "/issuer/authentication",
        },
        {
          name: t("tabs.organization"),
          path: "/issuer/organization",
        },
        {
          name: t("tabs.logout"),
          path: "/log-out",
        },
      ],
    },
  ];

  let userInfo = {
    name: `${user?.firstName} ${user?.lastName}`,
    avatar: user?.imageUrl ?? avatar,
    banner: user?.bannerUrl,
    userEmail: user?.email,
  };
  return (
    <div className="flex w-full">
      <Sidebar
        siteLogo={Images.SITE_LOGO}
        className={""}
        menuOptions={menuOptions}
        userInfo={userInfo}
      />
      {/* <DashboardIssuer /> */}
      <Outlet />
    </div>
  );
};
const VerifierDashboard = () => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const location = useLocation();

  const { user } = useAuth();

  let menuOptions = [
    {
      name: t("tabs.dashboard"),
      leftIcon: <DashboardIcon />,
      path: "/verifier/dashboard",
    },
    {
      name: t("tabs.initiateVerification"),
      leftIcon: <VerificationIcon />,
      path: "/verifier/initiate-verification",
    },
    {
      name: t("tabs.templates"),
      leftIcon: <ManageTemplateIcon />,
      path: "/verifier/manage-template",
    },
    {
      name: t("tabs.manageVerifications"),
      leftIcon: <ManageCredentails />,
      path: "/verifier/manage-verifications",
    },
    {
      name: t("tabs.analyticsAndReport"),
      leftIcon: <AnalyticsAndReport />,
      path: "/verifier/analytics",
    },
    {
      name: t("QR"),
      leftIcon: <QRIcon />,
      path: "/verifier/qr",
    },
    {
      name: t("tabs.settings"),
      leftIcon: <SettingsIcon />,
      subMenu: [
        {
          name: t("tabs.myProfile"),
          path: "/verifier/profile",
        },
        {
          name: t("tabs.authentication"),
          path: "/verifier/authentication",
        },
        {
          name: t("tabs.organization"),
          path: "/verifier/organization",
        },
        {
          name: t("tabs.logout"),
          path: "/log-out",
        },
      ],
    },
  ];

  let userInfo = {
    name: `${user?.firstName} ${user?.lastName}`,
    avatar: user?.imageUrl ?? avatar,
    banner: user?.bannerUrl,
    userEmail: user?.email,
  };
  return (
    <div className="flex w-full">
      <Sidebar
        siteLogo={Images.SITE_LOGO}
        className={""}
        menuOptions={menuOptions}
        userInfo={userInfo}
      />
      <Outlet />
    </div>
  );
};
const AdminDashboard = () => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const location = useLocation();

  const { user } = useAuth();

  let menuOptions = [
    {
      name: t("tabs.dashboard"),
      leftIcon: <DashboardIcon />,
      path: "/admin/dashboard",
    },
    {
      name: t("tabs.userManagement"),
      leftIcon: <ManageTemplateIcon />,
      path: "/admin/userManagement",
    },
    // {
    //   name: t("tabs.billingManagement"),
    //   leftIcon: <ManageTemplateIcon />,
    //   path: "/admin/billingManagement",
    // },
    {
      name: t("tabs.analyticsAndReport"),
      leftIcon: <AnalyticsAndReport />,
      path: "/admin/analytics",
    },
    {
      name: t("tabs.onBoarding"),
      leftIcon: <AnalyticsAndReport />,
      path: "/admin/holder-onboard",
    },
    {
      name: t("tabs.developer"),
      leftIcon: <DeveloperIcon />,
      subMenu: [
        {
          name: t("tabs.api"),
          path: "/admin/api",
        },
        {
          name: t("tabs.webhooks"),
          path: "/admin/webhooks",
        },
      ],
    },
    {
      name: t("tabs.settings"),
      leftIcon: <SettingsIcon />,
      subMenu: [
        {
          name: t("tabs.myProfile"),
          path: "/admin/profile",
        },
        {
          name: t("tabs.authentication"),
          path: "/admin/settings",
        },
        {
          name: t("tabs.organization"),
          path: "/admin/organization",
        },
        {
          name: t("tabs.logout"),
          path: "/log-out",
        },
      ],
    },
  ];

  let userInfo = {
    name: `${user?.firstName} ${user?.lastName}`,
    avatar: user?.imageUrl,
    userEmail: user?.email,
    banner: user?.bannerUrl,
  };
  return (
    <div className="flex w-full">
      <Sidebar
        siteLogo={Images.SITE_LOGO}
        className={""}
        menuOptions={menuOptions}
        userInfo={userInfo}
      />
      <Outlet />
    </div>
  );
};

const SuperAdmin = () => {
  const { t } = useTranslation();

  const { user } = useAuth();

  let menuOptions = [
    {
      name: t("tabs.dashboard"),
      leftIcon: <DashboardIcon />,
      path: "/superadmin/dashboard",
    },
    {
      name: t("tabs.customerManagement"),
      leftIcon: <UserManagementIcon />,
      path: "/superadmin/customerManagement",
    },
    // {
    //   name: t("tabs.billingManagement"),
    //   leftIcon: <ActivityIcon />,
    //   path: "/superadmin/billingManagement",
    // },
    {
      name: t("tabs.analyticsAndReport"),
      leftIcon: <AnalyticsAndReport />,
      path: "/superadmin/analytics",
    },
    {
      name: t("tabs.settings"),
      leftIcon: <SettingsIcon />,
      subMenu: [
        {
          name: t("tabs.myProfile"),
          path: "/superadmin/profile",
        },
        {
          name: t("tabs.logout"),
          path: "/log-out",
        },
      ],
    },
  ];

  let userInfo = {
    name: `${user?.firstName} ${user?.lastName}`,
    avatar: user?.imageUrl,
    userEmail: user?.email,
  };
  const isGradient = true;
  return (
    <div className="flex w-full">
      <Sidebar
        siteLogo={Images.SITE_LOGO}
        className={""}
        menuOptions={menuOptions}
        userInfo={userInfo}
        isGradient={isGradient}
      />
      <Outlet />
    </div>
  );
};

const RouteGuard = () => {
  const { user } = useAuth();
  return user ? <Outlet /> : <Navigate to={"/login"} replace />;
};

const RoleRouteGuard = ({ allowedRole = "" }) => {
  const { user } = useAuth();
  const userRole = user?.role || null;

  return userRole === allowedRole ? (
    <Outlet />
  ) : (
    <Navigate to={"/login"} replace />
  );
};

const PublicRoute = ({ allowedRole = "" }) => {
  const { user } = useAuth();

  if (user?.role) {
    const path =
      user.role === ROLES.Issuer
        ? "/issuer"
        : user.role === ROLES.Verifier
          ? "/verifier"
          : user.role === ROLES.Admin
            ? "/admin"
            : user.role === ROLES.SuperAdmin
              ? "/superadmin"
              : "/login";

    return <Navigate to={path} replace />;
  }

  // If no user, show public content
  return <Outlet />;
};

const App = () => {
  return (
    <div className="h-full min-h-screen bg-background-50">
      <Router>
        <AuthProvider>
          <ScrollToTop />
          <UserInActive />
          <IsPasswordResetIn60Days />
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="*" element={<NotFound />} />
              <Route path="/verification" element={<SharedCredential />} />
              <Route
                path="/termsAndConditions"
                element={<TermsAndConditions />}
              />
              <Route path="/privacyPolicy" element={<PrivacyPolicy />} />

              <Route element={<PublicRoute allowedRole={USER_ROLES.Issuer} />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginToCertify />} />
                <Route path="forgot-password" element={<ForgetPassword />} />
                <Route path="reset-password" element={<ResetPassword />} />
                <Route path="two-factor-auth" element={<TwoFactorAuth />} />
              </Route>
              {/* <Route path="/home" element={<Home />} /> */}

              <Route element={<RouteGuard />}>
                <Route
                  element={<RoleRouteGuard allowedRole={USER_ROLES.Issuer} />}
                >
                  <Route path="/issuer" element={<IssuerDashboard />}>
                    <Route index element={<Navigate to="dashboard" />} />
                    <Route path="dashboard" element={<DashboardIssuer />} />
                    <Route path="designs" element={<Certificate />} />
                    <Route
                      path="designs/editor/certificate"
                      element={<CertificateDesigner />}
                    />
                    <Route
                      path="designs/editor/badge"
                      element={<CertificateDesigner />}
                    />
                    <Route path="analytics" element={<ReportsAndAnalytics />} />
                    <Route
                      path="issue-credential"
                      element={<IssueCredential />}
                    />
                    <Route
                      path="manage-template"
                      element={<ManageTemplate />}
                    />
                    <Route
                      path="manage-credentials"
                      element={<ManageCredentials />}
                    />
                    <Route path="profile" element={<Profile />} />
                    <Route path="authentication" element={<Authentication />} />
                    <Route path="organization" element={<Organization />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Route>

                <Route
                  element={<RoleRouteGuard allowedRole={USER_ROLES.Verifier} />}
                >
                  <Route path="/verifier" element={<VerifierDashboard />}>
                    <Route index element={<Navigate to="dashboard" />} />
                    <Route path="dashboard" element={<DashboardVerifier />} />
                    <Route
                      path="initiate-verification"
                      element={<InitiateVerification />}
                    />
                    <Route path="manage-template" element={<ManageSchema />} />
                    <Route path="analytics" element={<AnalyticsAndReports />} />
                    <Route path="manage-verifications" element={<Verify />} />
                    <Route path="profile" element={<Profile />} />
                    <Route
                      path="authentication"
                      element={<VerifierAuthentication />}
                    />
                    <Route
                      path="organization"
                      element={<VerifierOrganization />}
                    />
                    <Route path="QR" element={<QR />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Route>

                <Route
                  element={<RoleRouteGuard allowedRole={USER_ROLES.Admin} />}
                >
                  <Route path="/admin" element={<AdminDashboard />}>
                    <Route index element={<Navigate to="dashboard" />} />
                    <Route path="dashboard" element={<AdminDashBoard />} />
                    <Route path="userManagement" element={<UserManagement />} />
                    {/* <Route
                      path="billingManagement"
                      element={<AdminBillingManagement />}
                    /> */}
                    <Route path="analytics" element={<AdminAnalyticsPage />} />
                    <Route
                      path="holder-onboard"
                      element={<HolderOnBoarding />}
                    />
                    <Route path="api" element={<DeveloperApi />} />
                    <Route path="webhooks" element={<DeveloperWebhook />} />
                    <Route
                      path="profile"
                      element={<Profile isbgActive={true} />}
                    />
                    <Route path="settings" element={<AdminAuthentication />} />
                    <Route
                      path="organization"
                      element={<AdminOrganization />}
                    />
                  </Route>
                </Route>

                <Route
                  element={
                    <RoleRouteGuard allowedRole={USER_ROLES.SuperAdmin} />
                  }
                >
                  <Route path="/superadmin" element={<SuperAdmin />}>
                    <Route index element={<Navigate to="dashboard" />} />
                    <Route path="dashboard" element={<SuperAdminDashBoard />} />
                    <Route
                      path="customerManagement"
                      element={<CustomerManagement />}
                    />
                    {/* <Route
                      path="billingManagement"
                      element={<BillingManagement />}
                    /> */}
                    <Route path="analytics" element={<SuperAdminAnalytics />} />
                    <Route path="profile" element={<Profile />} />
                  </Route>
                </Route>
              </Route>

              {/* <Route path="create-user" element={<CreateUserName />} /> */}
              {/* <Route path="login-final" element={<LoginFinalStep />} />
              <Route
                path="login-microsoft"
                element={<LoginToCertifyNextPage />}
              />
              <Route path="setting-up-profile" element={<SettingUpProfile />} /> */}
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
      <ToastContainer className="capitalize" style={{ zIndex: 99999 }} />
    </div>
  );
};

export default App;
